// ADMIN — Editor de la lista de compatibilidad de shaders (Firestore `shader_compat/main`).
// La lee la página de usuario /jartinet/comprobar-shaders. Fuente única: aquí se edita, allí se ve.

import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { DEFAULT_SHADERS, getShaderCompat, setShaderCompat, type ShaderRow } from '@/lib/shader-compat';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Sparkles, Plus, Trash2, Loader2, RotateCcw, Save, Check, X } from 'lucide-react';

export default function AdminShadersPage() {
  const { userData } = useAuth();
  const { toast } = useToast();
  const [rows, setRows] = useState<ShaderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getShaderCompat().then((r) => { setRows(r); setLoading(false); }); }, []);

  if (userData?.role !== 'admin') {
    return <p className="p-8 text-center text-muted-foreground">Solo administradores.</p>;
  }

  const set = (i: number, patch: Partial<ShaderRow>) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const del = (i: number) => setRows((rs) => rs.filter((_, j) => j !== i));
  const add = () => setRows((rs) => [...rs, { key: '', name: '', ok: true, ver: '' }]);

  const guardar = async () => {
    setSaving(true);
    const r = await setShaderCompat(rows);
    setSaving(false);
    toast({ title: r.success ? 'Guardado' : 'Error', description: r.message, variant: r.success ? undefined : 'destructive' });
  };

  return (
    <div className="space-y-6 pb-12">
      <div className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-primary/15 via-card/40 to-card/20 p-6">
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/20 blur-3xl" />
        <div className="relative flex items-center gap-4">
          <div className="grid h-14 w-14 place-items-center rounded-2xl border border-primary/30 bg-primary/15 text-primary">
            <Sparkles className="h-7 w-7" />
          </div>
          <div>
            <h1 className="font-headline text-3xl font-bold">Compatibilidad de shaders</h1>
            <p className="text-sm text-muted-foreground">
              Esta lista se muestra en <span className="font-mono text-xs">/jartinet/comprobar-shaders</span>.
            </p>
          </div>
        </div>
      </div>

      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="py-4 text-sm text-muted-foreground">
          <p><strong className="text-foreground">Nombre</strong>: cómo se muestra. <strong className="text-foreground">Clave</strong>: un
          trozo del nombre del archivo del shader en minúsculas y sin símbolos (ej. <span className="font-mono">bsl</span>,
          <span className="font-mono"> makeup</span>); es lo que usa el escáner para reconocerlo. <strong className="text-foreground">¿Va bien?</strong>: si
          el movimiento del palio se ve correcto.</p>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : (
        <>
          {/* Cabecera de columnas (solo desktop) */}
          <div className="hidden gap-2 px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground md:grid md:grid-cols-[1fr_140px_120px_110px_40px]">
            <span>Nombre</span><span>Clave</span><span>Versión</span><span>¿Va bien?</span><span></span>
          </div>

          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-1 gap-2 rounded-xl border border-white/8 bg-card/40 p-3 md:grid-cols-[1fr_140px_120px_110px_40px] md:items-center">
                <Input value={r.name} placeholder="Nombre" onChange={(e) => set(i, { name: e.target.value })} />
                <Input value={r.key} placeholder="clave" className="font-mono text-xs" onChange={(e) => set(i, { key: e.target.value })} />
                <Input value={r.ver} placeholder="versión" onChange={(e) => set(i, { ver: e.target.value })} />
                <button
                  onClick={() => set(i, { ok: !r.ok })}
                  className={`inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                    r.ok
                      ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-400'
                      : 'border-rose-500/40 bg-rose-500/15 text-rose-400'
                  }`}
                >
                  {r.ok ? <><Check className="h-4 w-4" /> Sí</> : <><X className="h-4 w-4" /> No</>}
                </button>
                <Button size="icon" variant="ghost" className="text-destructive justify-self-start md:justify-self-center" onClick={() => del(i)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={add}><Plus className="mr-2 h-4 w-4" /> Añadir shader</Button>
            <Button variant="ghost" onClick={() => setRows(DEFAULT_SHADERS)}>
              <RotateCcw className="mr-2 h-4 w-4" /> Restaurar la lista por defecto
            </Button>
            <div className="flex-1" />
            <Button onClick={guardar} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Guardar cambios
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
